<script>
/**
 * Card de envio do relatório, dentro do painel do Especialista Fee-Based.
 *
 * Copia a gramática do `ia-confirm-action` do IA CRM (preview → Confirmar /
 * Cancelar) sem importá-lo: aquele componente não tem campo editável, não
 * conhece progresso de longa duração e não fala de anexo. Enfiar as três coisas
 * lá dentro o transformaria num canivete.
 *
 * O componente **não chama API**: recebe o preview e emite intenção. Quem
 * orquestra geração + envio é o painel (`gerarEEnviar`). Assim a ação
 * irreversível tem um único dono.
 *
 * ★ [G-4, rodada-v5, QA 09/09/2026, achado da team-lead] Quando montado pelo
 * `ui-registry` (`ia/cartoes/cartao-envio-relatorio.js`), este cartão de
 * PREVIEW nunca recebe o desfecho (enviado/erro/descartado) na PRÓPRIA
 * parte — o servidor mina um `tool_call_id` NOVO para ele
 * (`servidor-confirmacao-<uuid>`/`protocolo-<uuid>`), exatamente o [G-17] do
 * `cartao-campos-reuniao`. Sem a correlação por `preview_id`
 * (`resolvidoExterno`, abaixo — mecanismo em
 * `fee-fixo-ia-envio-relatorio-desfecho-externo.js`), o cartão de origem
 * ficava preso no PREVIEW com "Cancelar"/"Enviar relatório" habilitados ao
 * lado do cartão de desfecho já resolvido. `previewId` é NOVO aqui — antes
 * chegava do adaptador sem ser prop declarada (virava atributo de DOM morto,
 * `$attrs` do `cartaoDoChat`). O painel do Especialista (montagem DIRETA,
 * fora do registry) não passa `previewId`: `resolvidoExterno` fica `''`
 * (fail-closed) e o card continua 100% governado pela prop `resolvido`
 * própria, como sempre foi — essa superfície já tem dono do próprio ciclo de
 * vida da requisição.
 *
 * ★ **[owner, 19/09/2026] A anatomia é o MOCK dele**, e ela tem três metades
 * que não são detalhe:
 *
 * 1. **O contexto vai para a DIREITA do título** (`Cliente (código) · cenário
 *    dd/mm/aaaa`), numa linha só. Eram dois blocos rótulo/valor empilhados
 *    ABAIXO do título, ocupando a faixa em que o formulário começa.
 * 2. **O formulário é em linhas ROTULADAS à esquerda** (`Para`, `Assunto`,
 *    `Mensagem`, `Anexo`), com os campos em `input-by-type` — o input unificado
 *    do repo — e o anexo como CHIP. O clipe antes era um `icon-svg` dentro de
 *    um `<p>`: a raiz dele é um `<div>`, então ele caía numa linha só sua e
 *    ficava ÓRFÃO acima do nome do arquivo (visto na vitrine).
 * 3. **O desfecho é RECIBO, nunca o formulário de novo.** Medido na vitrine:
 *    `approval-requested`, `output-available` e `output-error` desenhavam os
 *    três IDÊNTICOS — "Cancelar/Enviar relatório" sobre um envio já decidido.
 *    Hoje o `state` do turno entra como última fonte de desfecho (ver
 *    `RESOLVIDO_POR_ESTADO_DO_TURNO`), e o recibo é a faixa de duas linhas com
 *    o ponto de cor à esquerda e o selo à direita — a mesma forma da
 *    confirmação de gravação (`ia-confirm-action`), que é o padrão da casa
 *    para "já aconteceu".
 */
import {
  mensagensDaSuperficie,
  resolvidoExternoDoPreview,
} from './fee-fixo-ia-envio-relatorio-desfecho-externo'

// Mesma validação do cliente de API — de propósito: o botão precisa desabilitar
// antes de qualquer request, e a validação que vale continua sendo a do backend.
const FORMATO_EMAIL = /^[^@\s,;]+@[^@\s,;]+\.[^@\s,;]{2,}$/

const TEXTO_POR_ETAPA = {
  gerando: 'Gerando o relatório… isso costuma levar cerca de 1 minuto.',
  enviando: 'Enviando o e-mail…',
}

const ERRO_GENERICO =
  'Não foi possível enviar o relatório. Tente novamente em instantes.'

// A nota do rodapé, na voz do mock: a origem do texto e a cópia oculta numa
// linha só, à esquerda do par de ações.
const NOTA_TEXTO_PADRAO =
  'Texto padrão da AAWZ (o escritório não cadastrou um modelo).'
const NOTA_COPIA_OCULTA = 'Você recebe uma cópia oculta.'
const RECIBO_COPIA_OCULTA = 'cópia oculta para você'

// Os selos do desfecho, à direita da faixa. Mesma FORMA do `statusText` do
// `ia-confirm-action` ("Confirmado" / "Cancelado · nada gravado"), com o
// vocabulário DESTE cartão — ele envia, não grava.
const SELO_ENVIADO = 'Enviado'
const SELO_CANCELADO = 'Cancelado · nada enviado'

/**
 * ★ [owner, 19/09/2026] Os estados do TURNO que resolvem esta parte.
 *
 * Contrato: `core/sse_tool_result.estado_do_tool_result` marca
 * `approval-requested` quando o `value` tem `requer_confirmacao` — que é o
 * PREVIEW — e `output-available` no resto. Ou seja, `output-available` nesta
 * parte é o DESFECHO, e nele o cartão é recibo, nunca o formulário de novo.
 *
 * É fallback, não a fonte: quando o payload nomeia o desfecho (`estado`), quem
 * manda é ele — só assim `descartado` e `erro` não viram "enviado".
 */
const RESOLVIDO_POR_ESTADO_DO_TURNO = {
  'output-available': 'enviado',
  'output-denied': 'cancelado',
  'output-error': 'erro',
}

export default {
  name: 'FeeFixoIaEnvioRelatorio',
  props: {
    preview: {
      type: Object,
      required: true,
    },
    etapa: {
      type: String,
      default: '',
    },
    resolvido: {
      type: String,
      default: '',
    },
    mensagemErro: {
      type: String,
      default: '',
    },
    resultado: {
      type: Object,
      default: null,
    },
    // ★ [G-4] O id que autoriza correlacionar o desfecho de OUTRA mensagem
    // com este cartão de origem — mesmo papel de `previewId` em
    // `cartao-campos-reuniao.vue`. Ausente na montagem direta pelo painel do
    // Especialista (ver o cabeçalho acima).
    previewId: {
      type: String,
      default: '',
    },
    /**
     * O estado da parte do turno (moldura — `CHAVES_DA_MOLDURA`). Vazio é a
     * montagem DIRETA pelo painel do Especialista, que orquestra o próprio
     * ciclo de vida e continua governado pela prop `resolvido`.
     */
    state: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      destinatario: (this.preview && this.preview.destinatario_sugerido) || '',
      // [spec-021, T005] O valor sugerido pelo backend é o ponto de partida —
      // o consultor reescreve por cima. `campos_confirmados` no envio leva só
      // o que ele mudou de fato (ver `camposEditados`).
      assunto: (this.preview && this.preview.assunto) || '',
      corpo: (this.preview && this.preview.corpo) || '',
      tocado: false,
      selos: { enviado: SELO_ENVIADO, cancelado: SELO_CANCELADO },
    }
  },
  computed: {
    processando() {
      return this.etapa === 'gerando' || this.etapa === 'enviando'
    },
    textoDaEtapa() {
      return TEXTO_POR_ETAPA[this.etapa] || ''
    },
    emailNormalizado() {
      return (this.destinatario || '').trim().toLowerCase()
    },
    destinatarioValido() {
      return FORMATO_EMAIL.test(this.emailNormalizado)
    },
    erroDoCampo() {
      if (!this.tocado || this.destinatarioValido) return ''
      if (!this.emailNormalizado) return 'Informe o e-mail do destinatário.'
      if (this.emailNormalizado.indexOf(',') >= 0 || this.emailNormalizado.indexOf(';') >= 0) {
        return 'Envie para um destinatário por vez.'
      }
      return 'E-mail inválido. Formato esperado: nome@dominio.com.br.'
    },
    assuntoValido() {
      return !!(this.assunto || '').trim()
    },
    corpoValido() {
      return !!(this.corpo || '').trim()
    },
    erroDoAssunto() {
      if (!this.tocado || this.assuntoValido) return ''
      return 'O assunto não pode ficar em branco.'
    },
    erroDoCorpo() {
      if (!this.tocado || this.corpoValido) return ''
      return 'A mensagem não pode ficar em branco.'
    },
    /**
     * Só o que MUDOU em relação ao preview. Edição é delta: mandar tudo
     * sempre faria o servidor revalidar (e possivelmente recusar) campos que
     * a pessoa nem tocou.
     */
    camposEditados() {
      const editados = {}
      const sugerido = this.preview || {}
      if (this.emailNormalizado !== (sugerido.destinatario_sugerido || '')) {
        editados.destinatario = this.emailNormalizado
      }
      if ((this.assunto || '') !== (sugerido.assunto || '')) {
        editados.assunto = (this.assunto || '').trim()
      }
      if ((this.corpo || '') !== (sugerido.corpo || '')) {
        editados.corpo = (this.corpo || '').trim()
      }
      return editados
    },
    podeEnviar() {
      return (
        this.destinatarioValido &&
        this.assuntoValido &&
        this.corpoValido &&
        !this.processando &&
        !this.resolvidoQualquer
      )
    },
    /**
     * ★ [G-4] O `resolvido` que OUTRA mensagem já publicou para este
     * `previewId`, lido nas três superfícies de chat conhecidas do store —
     * `''` sem match (fail-closed: sem `previewId`, nenhuma correlação é
     * possível). Ver `fee-fixo-ia-envio-relatorio-desfecho-externo.js`.
     */
    resolvidoExterno() {
      if (!this.previewId) return ''
      const mensagens = mensagensDaSuperficie(
        this.$store && this.$store.state
      )
      return resolvidoExternoDoPreview(mensagens, this.previewId)
    },
    /**
     * Une o desfecho NATIVO (prop `resolvido` própria — commit no payload
     * deste cartão, ou orquestração local do painel) com o EXTERNO (acima) —
     * o par Cancelar/Enviar não pode continuar oferecido em nenhum dos dois.
     * A prop nativa VENCE quando as duas existirem (não deveria acontecer:
     * ou o cartão é o de origem, ou é o do desfecho — nunca os dois ao
     * mesmo tempo).
     */
    resolvidoQualquer() {
      return this.resolvido || this.resolvidoExterno || this.resolvidoPeloTurno
    },
    /**
     * ★ [owner, 19/09/2026] O TURNO já decidiu, e o payload desta parte não
     * nomeou o desfecho. Última fonte das três, de propósito: `resolvido` (o
     * payload) e `resolvidoExterno` (o desfecho de outra mensagem, pelo
     * `preview_id`) sabem QUAL foi o desfecho; este só sabe que houve um.
     */
    resolvidoPeloTurno() {
      return RESOLVIDO_POR_ESTADO_DO_TURNO[this.state] || ''
    },
    avisaTextoPadrao() {
      return this.preview && this.preview.usou_template_escritorio === false
    },
    avisoDoCadastro() {
      return (this.preview && this.preview.aviso_destinatario) || ''
    },
    identificacaoDoCliente() {
      const nome = this.preview && this.preview.nome_cliente
      const codigo = this.preview && this.preview.codigo_cliente
      if (nome && nome !== codigo) return `${nome} (${codigo})`
      return `Cliente ${codigo}`
    },
    textoDoErro() {
      // Mensagem vazia é exatamente o que produz o "Erro inesperado" genérico.
      return this.mensagemErro || ERRO_GENERICO
    },
    destinatarioEnviado() {
      return (this.resultado && this.resultado.destinatario) || this.emailNormalizado
    },
    /**
     * O contexto do cartão, em UMA linha à direita do título (mock do owner):
     * de quem é o relatório e de qual cenário. Eram dois blocos rótulo/valor
     * empilhados sob o título, que é o espaço em que o formulário começa.
     */
    contextoDoCartao() {
      const cenario = this.preview && this.preview.data_do_cenario
      if (!cenario) return this.identificacaoDoCliente
      return this.identificacaoDoCliente + ' · cenário ' + cenario
    },
    /** A nota do rodapé — a origem do texto só entra quando ela é fato. */
    notaDoRodape() {
      if (!this.avisaTextoPadrao) return NOTA_COPIA_OCULTA
      return NOTA_TEXTO_PADRAO + ' ' + NOTA_COPIA_OCULTA
    },
    /** A segunda linha do recibo: para quem foi, o que foi, e a cópia oculta. */
    linhaDoRecibo() {
      const anexo = (this.preview && this.preview.nome_anexo) || ''
      const partes = [this.destinatarioEnviado, anexo, RECIBO_COPIA_OCULTA]
      return partes.filter(Boolean).join(' · ')
    },
  },
  methods: {
    marcarTocado() {
      this.tocado = true
    },
    solicitarEnvio() {
      this.tocado = true
      if (!this.podeEnviar) return
      this.$emit('enviar', {
        destinatario: this.emailNormalizado,
        // [spec-021, T005] O que o consultor reescreveu. Vira
        // `campos_confirmados` no POST (`apiChat.responderConfirmacao`) e o
        // servidor valida contra `campos_editaveis` do preview registrado.
        camposConfirmados: this.camposEditados,
      })
    },
    cancelar() {
      this.$emit('cancelar')
    },
    fechar() {
      this.$emit('fechar')
    },
  },
}
</script>

<template>
  <!-- ★ [owner, 19/09/2026] A anatomia é o mock dele: cabeçalho com o resumo do
       contexto à DIREITA, formulário em linhas rotuladas à esquerda, anexo como
       CHIP, e o rodapé com a nota à esquerda e o par de ações à direita. O
       desfecho é o RECIBO de uma linha, no formato da confirmação de gravação —
       nunca o formulário de novo.
       `ia-block-card` é a moldura da família: hairline, `--radius-lg`, branco e
       SEM sombra (`.az-card` traz `@extend .az-shadow` e raio de 6px). -->
  <div class="ff-envio az-text-ff-envio ia-block-card pa-4">
    <!-- ERRO -->
    <template v-if="resolvidoQualquer === 'erro'">
      <div class="d-flex align-center az-gap-small">
        <span
          class="ff-envio__ponto flex-shrink-0"
          :style="{ background: 'var(--danger)' }"
        />
        <span class="az-text-primary az-size-14 font-semibold">
          Não foi possível enviar
        </span>
      </div>
      <p class="ff-envio__texto az-size-12 mb-0 mt-1">{{ textoDoErro }}</p>
      <div class="d-flex justify-end mt-3">
        <BaseButton underlined @click="fechar">Fechar</BaseButton>
      </div>
    </template>

    <!-- ENVIADO — o recibo compacto do mock: duas linhas e o selo à direita. -->
    <template v-else-if="resolvidoQualquer === 'enviado'">
      <div class="d-flex align-center az-gap-small">
        <span
          class="ff-envio__ponto flex-shrink-0"
          :style="{ background: 'var(--success)' }"
        />
        <span class="az-text-primary az-size-14 font-semibold">
          Relatório enviado
        </span>
        <output
          data-teste="envio-selo"
          class="ml-auto az-text-primary az-size-12 az-muted text-no-wrap"
          aria-live="polite"
        >
          {{ selos.enviado }}
        </output>
      </div>
      <p
        data-teste="envio-recibo"
        class="ff-envio__texto az-size-12 mb-0 mt-1"
      >
        {{ linhaDoRecibo }}
      </p>
    </template>

    <!-- CANCELADO -->
    <template v-else-if="resolvidoQualquer === 'cancelado'">
      <div class="d-flex align-center az-gap-small">
        <span
          class="ff-envio__ponto flex-shrink-0"
          :style="{ background: 'var(--neutral-400)' }"
        />
        <span class="az-text-primary az-size-14 font-semibold">
          Envio cancelado.
        </span>
        <output
          class="ml-auto az-text-primary az-size-12 az-muted text-no-wrap"
          aria-live="polite"
        >
          {{ selos.cancelado }}
        </output>
      </div>
    </template>

    <!-- LOADING -->
    <template v-else-if="processando">
      <div class="ff-envio__cabecalho d-flex align-center az-gap-small mb-3">
        <span
          class="ff-envio__ponto flex-shrink-0"
          :style="{ background: 'var(--primary)' }"
        />
        <span class="az-text-primary az-size-14 font-semibold">
          Enviar relatório por e-mail
        </span>
        <span class="ml-auto ff-envio__texto az-size-12 text-no-wrap">
          {{ contextoDoCartao }}
        </span>
      </div>

      <div class="d-flex align-center mb-4" aria-live="polite">
        <v-progress-circular indeterminate size="18" width="2" />
        <span class="ff-envio__texto ml-3">{{ textoDaEtapa }}</span>
      </div>

      <div class="d-flex justify-end">
        <BaseButton underlined @click="cancelar">Cancelar</BaseButton>
      </div>
    </template>

    <!-- PREVIEW -->
    <template v-else>
      <!-- Cabeçalho: o que a ação É à esquerda, o CONTEXTO dela à direita. -->
      <div class="ff-envio__cabecalho d-flex align-center az-gap-small mb-4">
        <span
          class="ff-envio__ponto flex-shrink-0"
          :style="{ background: 'var(--primary)' }"
        />
        <span class="az-text-primary az-size-14 font-semibold">
          Enviar relatório por e-mail
        </span>
        <span
          data-teste="envio-contexto"
          class="ml-auto ff-envio__texto az-size-12 text-no-wrap"
        >
          {{ contextoDoCartao }}
        </span>
      </div>

      <!-- Linhas rotuladas: rótulo à esquerda, campo à direita — os campos são
           `input-by-type`, o input unificado do repo. -->
      <div class="ff-envio__linha d-flex align-start mb-3">
        <label for="ff-envio-destinatario" class="ff-envio__rotulo az-text-ff-envio-rotulo">
          Para
        </label>
        <div class="ff-envio__campo">
          <input-by-type
            id="ff-envio-destinatario"
            id-input="ff-envio-destinatario"
            filled
            dense
            class="no-border"
            type="text"
            :value="destinatario"
            placeholder="e-mail do cliente"
            :error-messages="erroDoCampo"
            aria-describedby="ff-envio-destinatario-erro"
            @input="destinatario = $event"
            @blur="marcarTocado"
          />
          <p
            v-if="erroDoCampo"
            id="ff-envio-destinatario-erro"
            class="az-text-ff-envio-erro mb-0"
          >
            {{ erroDoCampo }}
          </p>
          <p v-else-if="avisoDoCadastro" class="az-text-small mb-0">
            {{ avisoDoCadastro }}
          </p>
        </div>
      </div>

      <!-- [spec-021, T005] Editáveis desde que o valor digitado VIAJA até o
           commit (`campos_confirmados` no POST + validação no servidor contra
           `campos_editaveis` do preview). Antes disto eram somente-leitura,
           e com razão: campo que edita e não grava é mentira de interface. -->
      <div class="ff-envio__linha d-flex align-start mb-3">
        <label for="ff-envio-assunto" class="ff-envio__rotulo az-text-ff-envio-rotulo">
          Assunto
        </label>
        <div class="ff-envio__campo">
          <input-by-type
            id="ff-envio-assunto"
            id-input="ff-envio-assunto"
            filled
            dense
            class="no-border"
            type="text"
            :value="assunto"
            placeholder="assunto do e-mail"
            :error-messages="erroDoAssunto"
            @input="assunto = $event"
            @blur="marcarTocado"
          />
        </div>
      </div>

      <div class="ff-envio__linha d-flex align-start mb-3">
        <label for="ff-envio-corpo" class="ff-envio__rotulo az-text-ff-envio-rotulo">
          Mensagem
        </label>
        <div class="ff-envio__campo">
          <input-by-type
            id="ff-envio-corpo"
            id-input="ff-envio-corpo"
            filled
            dense
            class="no-border"
            type="textarea"
            :value="corpo"
            :rows="6"
            placeholder="mensagem do e-mail"
            :error-messages="erroDoCorpo"
            @input="corpo = $event"
            @blur="marcarTocado"
          />
        </div>
      </div>

      <!-- ⚠️ O anexo é CHIP, não uma linha de texto com um clipe solto: a raiz
           do `icon-svg` é um `<div>`, e no parágrafo anterior ele caía numa
           linha só dele — o clipe órfão flutuando acima do nome do arquivo. -->
      <div class="ff-envio__linha d-flex align-center mb-4">
        <span class="ff-envio__rotulo az-text-ff-envio-rotulo">Anexo</span>
        <div class="ff-envio__campo">
          <span data-teste="envio-anexo" class="ff-envio__anexo d-inline-flex align-center az-gap-xsmall">
            <icon-svg name="mdi-paperclip" :size="14" color="var(--text-muted)" />
            <span class="az-size-12">{{ preview.nome_anexo }}</span>
          </span>
        </div>
      </div>

      <!-- Rodapé: a nota à esquerda, as ações à direita, e a PRIMÁRIA por
           último (régua no cabeçalho de `ui-registry/cartao-casca.vue`). -->
      <div class="d-flex align-center az-gap-small">
        <span class="ff-envio__texto az-size-12">{{ notaDoRodape }}</span>
        <div class="ml-auto d-flex align-center az-gap-small">
          <BaseButton bordered @click="cancelar">Cancelar</BaseButton>
          <BaseButton primary :disabled="!podeEnviar" @click="solicitarEnvio">
            Enviar relatório
          </BaseButton>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="sass" scoped>
// Sobrou só o que é estrutura deste card, não tipografia: a tipografia mora em
// `src/design/aawz/az-text.sass` (az-text-ff-envio*, az-text-small,
// font-semibold), pela regra de nao duplicar utilitario em style scoped.
.ff-envio__texto
  color: var(--muted)

// ★ [owner, 19/09/2026] A coluna de RÓTULOS do formulário. Só a largura mora
// aqui — alinhamento e eixo vêm das utilitárias no template, como manda o DS.
// 84px é o que "Mensagem" pede sem quebrar, com um degrau de folga.
.ff-envio__rotulo
  flex: 0 0 84px
  padding-top: 10px

.ff-envio__campo
  flex: 1 1 auto
  // ★ [owner, 20/09/2026] O campo do mock é CAIXA PREENCHIDA e arredondada:
  // `filled` do Vuetify dá o fundo mas arredonda só o topo, e `.no-border`
  // (global) mata só o `::before`. O `::after` é a linha de FOCO na cor da
  // marca — deixá-la desenharia um sublinhado que o mock não tem.
  ::v-deep .v-input__slot
    border-radius: var(--radius-sm)
    background: var(--neutral-50) !important
  ::v-deep .v-input__slot::after
    display: none
  // Sem isto o textarea largo estica a linha e o cartão ganha rolagem
  // horizontal — item flex sem `min-width` usa a largura do conteúdo.
  min-width: 0

// O anexo é um CHIP: hairline, raio e o nome do arquivo ao lado do clipe.
.ff-envio__anexo
  padding: 4px 10px
  border: 1px solid var(--border-hairline)
  border-radius: var(--radius-sm)
  color: var(--neutral-700)

// O ponto de cor do desfecho — a cor vem do template (token), porque é ela
// que DIZ qual foi o desfecho.
.ff-envio__ponto
  width: 8px
  height: 8px
  border-radius: 50%
.ff-envio__corpo
  color: var(--neutral-color-700)
  // ★ [G-7, QA rodada-v5, 09/09/2026, achado da team-lead] `line-height`
  // EXPLÍCITO — a folha global não declara um para este bloco, e sem ele
  // `max-height` em rem não é múltiplo de NADA mensurável: a última linha
  // visível do corpo do e-mail cortava ao meio. `max-height` é exatamente
  // 5 linhas (5 × line-height = 100px = 6.25rem) — nunca corta no meio, e
  // mantém a leitura em ~5 linhas do original (6,5rem ~ 5,2 linhas).
  line-height: 20px
  max-height: 6.25rem
  overflow-y: auto
  white-space: pre-line
</style>
